import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { businessService } from '../utils/supabaseService';
import { BUSINESS_CATEGORY_LABELS, formatDate, truncateText, getCategoryColor, getCategoryIcon } from '../utils/smartCitiesData';

const BusinessDirectoryPage = () => {
  const [businesses, setBusinesses] = useState([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    city: '',
    featured: false
  });

  // Load businesses
  useEffect(() => {
    loadBusinesses();
  }, []);

  // Apply filters
  useEffect(() => {
    applyFilters();
  }, [businesses, filters]);

  const loadBusinesses = async () => {
    setLoading(true);
    try {
      const { data, error } = await businessService?.getAllBusinesses();
      if (!error && data) {
        setBusinesses(data);
      }
    } catch (error) {
      console.error('Error loading businesses:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...businesses];

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter(business =>
        business?.name?.toLowerCase()?.includes(searchLower) ||
        business?.description?.toLowerCase()?.includes(searchLower) ||
        business?.services?.some(service => service?.toLowerCase()?.includes(searchLower))
      );
    }

    if (filters?.category) {
      filtered = filtered?.filter(business => business?.category === filters?.category);
    }

    if (filters?.city) {
      filtered = filtered?.filter(business =>
        business?.city?.toLowerCase()?.includes(filters?.city?.toLowerCase())
      );
    }

    if (filters?.featured) {
      filtered = filtered?.filter(business => business?.is_featured);
    }

    setFilteredBusinesses(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleBusinessClick = async (businessId) => {
    try {
      const { data, error } = await businessService?.getBusiness(businessId);
      if (!error && data) {
        setSelectedBusiness(data);
        setShowModal(true);
      }
    } catch (error) {
      console.error('Error loading business details:', error);
    }
  };

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...Object.entries(BUSINESS_CATEGORY_LABELS)?.map(([value, label]) => ({ value, label }))
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading businesses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-foreground mb-4">Business Directory</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover innovative companies, startups, and organizations driving smart cities development
            </p>
          </div>

          {/* Filters */}
          <div className="bg-background border border-border rounded-lg p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Input
                placeholder="Search businesses..."
                value={filters?.search}
                onChange={(e) => handleFilterChange('search', e?.target?.value)}
                iconName="Search"
              />
              <Select
                placeholder="Select category"
                options={categoryOptions}
                value={filters?.category}
                onChange={(value) => handleFilterChange('category', value)}
              />
              <Input
                placeholder="City"
                value={filters?.city}
                onChange={(e) => handleFilterChange('city', e?.target?.value)}
                iconName="MapPin"
              />
              <div className="flex items-center space-x-4">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters?.featured}
                    onChange={(e) => handleFilterChange('featured', e?.target?.checked)}
                    className="w-4 h-4 text-primary bg-gray-100 border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">Featured only</span>
                </label>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between mt-6">
            <p className="text-sm text-muted-foreground">
              Found {filteredBusinesses?.length} {filteredBusinesses?.length === 1 ? 'business' : 'businesses'}
            </p>
            <Button variant="outline" size="sm">
              <Icon name="SlidersHorizontal" size={16} className="mr-2" />
              More Filters
            </Button>
          </div>
        </div>
      </div>
      {/* Business Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {filteredBusinesses?.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredBusinesses?.map((business) => (
              <motion.div
                key={business?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -2 }}
                className="bg-card border border-border rounded-lg p-6 cursor-pointer transition-shadow hover:shadow-md"
                onClick={() => handleBusinessClick(business?.id)}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    {business?.logo_url ? (
                      <img
                        src={business?.logo_url}
                        alt={`${business?.name} logo`}
                        className="w-12 h-12 rounded-lg object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name={getCategoryIcon(business?.category)} size={24} className="text-primary" />
                      </div>
                    )}
                    <div>
                      <h3 className="font-semibold text-foreground">{business?.name}</h3>
                      <p className="text-sm text-muted-foreground">{business?.owner?.full_name}</p>
                    </div>
                  </div>
                  {business?.is_featured && (
                    <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">
                      Featured
                    </div>
                  )}
                </div>

                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mb-3 ${getCategoryColor(business?.category)}`}>
                  <Icon name={getCategoryIcon(business?.category)} size={14} className="mr-1" />
                  {BUSINESS_CATEGORY_LABELS?.[business?.category]}
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                  {truncateText(business?.description, 120)}
                </p>

                <div className="flex items-center text-xs text-muted-foreground mb-4">
                  <Icon name="MapPin" size={14} className="mr-1" />
                  {business?.city}, {business?.country}
                  <span className="mx-2">•</span>
                  <Icon name="Eye" size={14} className="mr-1" />
                  {business?.view_count} views
                </div>

                {business?.services?.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {business?.services?.slice(0, 3)?.map((service, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs"
                      >
                        {service}
                      </span>
                    ))}
                    {business?.services?.length > 3 && (
                      <span className="text-xs text-muted-foreground">
                        +{business?.services?.length - 3} more
                      </span>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Building2" size={64} className="text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">No businesses found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your filters or search criteria
            </p>
            <Button variant="outline" onClick={() => setFilters({ search: '', category: '', city: '', featured: false })}>
              Clear Filters
            </Button>
          </div>
        )}
      </div>
      {/* Business Detail Modal */}
      {showModal && selectedBusiness && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-card rounded-lg shadow-lg max-w-4xl w-full max-h-screen overflow-y-auto"
          >
            <div className="sticky top-0 bg-card border-b border-border p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  {selectedBusiness?.logo_url ? (
                    <img
                      src={selectedBusiness?.logo_url}
                      alt={`${selectedBusiness?.name} logo`}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={getCategoryIcon(selectedBusiness?.category)} size={32} className="text-primary" />
                    </div>
                  )}
                  <div>
                    <h2 className="text-2xl font-bold text-foreground">{selectedBusiness?.name}</h2>
                    <p className="text-muted-foreground">{selectedBusiness?.owner?.full_name}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowModal(false)}
                >
                  <Icon name="X" size={24} />
                </Button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">About</h3>
                <p className="text-muted-foreground">{selectedBusiness?.description}</p>
              </div>

              {/* Contact Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Contact Information</h3>
                  <div className="space-y-2">
                    {selectedBusiness?.contact_email && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Icon name="Mail" size={16} className="text-muted-foreground" />
                        <a 
                          href={`mailto:${selectedBusiness?.contact_email}`}
                          className="text-primary hover:underline"
                        >
                          {selectedBusiness?.contact_email}
                        </a>
                      </div>
                    )}
                    {selectedBusiness?.contact_phone && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Icon name="Phone" size={16} className="text-muted-foreground" />
                        <span className="text-foreground">{selectedBusiness?.contact_phone}</span>
                      </div>
                    )}
                    {selectedBusiness?.website_url && (
                      <div className="flex items-center space-x-2 text-sm">
                        <Icon name="Globe" size={16} className="text-muted-foreground" />
                        <a 
                          href={selectedBusiness?.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:underline"
                        >
                          Visit Website
                        </a>
                      </div>
                    )}
                    <div className="flex items-center space-x-2 text-sm">
                      <Icon name="MapPin" size={16} className="text-muted-foreground" />
                      <span className="text-foreground">
                        {selectedBusiness?.address && `${selectedBusiness?.address}, `}
                        {selectedBusiness?.city}, {selectedBusiness?.country}
                      </span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getCategoryColor(selectedBusiness?.category)}`}>
                      <Icon name={getCategoryIcon(selectedBusiness?.category)} size={16} className="mr-2" />
                      {BUSINESS_CATEGORY_LABELS?.[selectedBusiness?.category]}
                    </div>
                    {selectedBusiness?.founded_year && (
                      <p className="text-muted-foreground">
                        Founded: {selectedBusiness?.founded_year}
                      </p>
                    )}
                    {selectedBusiness?.employee_count_range && (
                      <p className="text-muted-foreground">
                        Employees: {selectedBusiness?.employee_count_range}
                      </p>
                    )}
                    <p className="text-muted-foreground">
                      Member since: {formatDate(selectedBusiness?.created_at)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Services */}
              {selectedBusiness?.services?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Services</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedBusiness?.services?.map((service, index) => (
                      <span
                        key={index}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {service}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Technologies */}
              {selectedBusiness?.technologies?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Technologies</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedBusiness?.technologies?.map((tech, index) => (
                      <span
                        key={index}
                        className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Owner Info */}
              {selectedBusiness?.owner && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-3">Contact Person</h3>
                  <div className="flex items-center space-x-3">
                    {selectedBusiness?.owner?.profile_image_url ? (
                      <img
                        src={selectedBusiness?.owner?.profile_image_url}
                        alt={selectedBusiness?.owner?.full_name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <span className="text-primary font-medium">
                          {selectedBusiness?.owner?.full_name?.charAt(0)?.toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-foreground">{selectedBusiness?.owner?.full_name}</p>
                      <p className="text-sm text-muted-foreground">{selectedBusiness?.owner?.title}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default BusinessDirectoryPage;